export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  featured_image: string;
  image_url?: string;
  author: string;
  status: string;
  published_at: string;
  created_at: string;
  updated_at: string;
}

export interface BlogResponse {
  status: boolean;
  data: BlogPost[];
  category_counts?: Record<string, number>;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    next_page_url: string | null;
    prev_page_url: string | null;
  };
}

export interface SingleBlogResponse {
  status: boolean;
  data: {
    blog: BlogPost;
    relatedBlogs: BlogPost[];
    seo?: any;
  };
}

const resolveApiUrl = () => {
  const configured = import.meta.env.VITE_BLOG_API_URL?.trim();
  if (configured) {
    return configured.endsWith('/') ? configured : `${configured}/`;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (window.location.port === '5173') {
      return `http://${host}:8000/api/`;
    }
    return `${window.location.origin}/api/`;
  }

  return '/api/';
};

const API_URL = resolveApiUrl();
const BASE_IMAGE_URL = API_URL.replace(/\/api\/?$/, '/');
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=2072';

const blogDetailCache = new Map<string, SingleBlogResponse>();
const blogSummaryCache = new Map<string, BlogPost>();

// Warm summary cache from persistent storage if available
try {
  if (typeof window !== 'undefined') {
    const raw = sessionStorage.getItem('prince_blog_cache_v2') || localStorage.getItem('prince_blog_cache_v2');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.blogs)) {
        parsed.blogs.forEach((b: BlogPost) => {
          if (b && b.slug) blogSummaryCache.set(b.slug, b);
        });
      }
    }
  }
} catch (_) {}

export const blogService = {
  getCachedBlogDetail(slug: string): SingleBlogResponse | undefined {
    if (!slug) return undefined;
    return (
      blogDetailCache.get(slug) ||
      blogDetailCache.get(decodeURIComponent(slug)) ||
      blogDetailCache.get(encodeURIComponent(slug))
    );
  },

  getCachedBlogSummary(slug: string): BlogPost | undefined {
    if (!slug) return undefined;
    return (
      blogSummaryCache.get(slug) ||
      blogSummaryCache.get(decodeURIComponent(slug)) ||
      blogSummaryCache.get(encodeURIComponent(slug))
    );
  },

  setCachedBlogSummary(blog: BlogPost) {
    if (blog && blog.slug) {
      blogSummaryCache.set(blog.slug, blog);
    }
  },

  async getBlogs(page = 1, perPage = 9, category = 'all', search = ''): Promise<BlogResponse> {
    const apiUrl = resolveApiUrl();
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });

    if (category && category !== 'all') {
      params.set('category', category);
    }
    if (search && search.trim()) {
      params.set('search', search.trim());
    }

    const response = await fetch(`${apiUrl}blogs?${params.toString()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data: BlogResponse = await response.json();
    if (data && Array.isArray(data.data)) {
      data.data.forEach((b) => {
        if (b.slug) {
          blogSummaryCache.set(b.slug, b);
        }
      });
    }

    return data;
  },

  async getBlogBySlug(slug: string): Promise<SingleBlogResponse> {
    const apiUrl = resolveApiUrl();
    const cleanSlug = encodeURIComponent(decodeURIComponent(slug));

    const cached = blogDetailCache.get(slug) || blogDetailCache.get(cleanSlug);
    if (cached) {
      // Background revalidate
      fetch(`${apiUrl}blog/${cleanSlug}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && data.status) {
            blogDetailCache.set(slug, data);
            blogDetailCache.set(cleanSlug, data);
            if (data.data?.blog?.slug) {
              blogSummaryCache.set(data.data.blog.slug, data.data.blog);
            }
          }
        })
        .catch(() => {});
      return cached;
    }

    const response = await fetch(`${apiUrl}blog/${cleanSlug}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      // If encoded slug fails and differs from raw slug, try raw slug once
      if (cleanSlug !== slug) {
        const rawRes = await fetch(`${apiUrl}blog/${slug}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (rawRes.ok) {
          const data: SingleBlogResponse = await rawRes.json();
          if (data && data.status) {
            blogDetailCache.set(slug, data);
            blogDetailCache.set(cleanSlug, data);
            if (data.data?.blog?.slug) {
              blogSummaryCache.set(data.data.blog.slug, data.data.blog);
            }
          }
          return data;
        }
      }
      throw new Error(`Failed to fetch blog post (${response.status})`);
    }

    const result: SingleBlogResponse = await response.json();
    if (result && result.status) {
      blogDetailCache.set(slug, result);
      blogDetailCache.set(cleanSlug, result);
      if (result.data?.blog?.slug) {
        blogSummaryCache.set(result.data.blog.slug, result.data.blog);
      }
    }

    return result;
  },

  getFeaturedImageUrl(path: string | undefined | null, canonicalUrl?: string | undefined | null): string {
    if (canonicalUrl) return canonicalUrl;
    if (!path) return FALLBACK_IMAGE;
    if (path.startsWith('http')) return path;
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const cleanBase = BASE_IMAGE_URL.endsWith('/') ? BASE_IMAGE_URL : `${BASE_IMAGE_URL}/`;
    return `${cleanBase}${cleanPath}`;
  }
};
